import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getVersionTypeLabel, versionTypeEnum } from '../../../../constants';
import { useCreateVersionNoteMutation, useUpdateVersionNoteMutation } from '../../../../hooks/mutations/useVersionNoteMutation';
import { VersionNote } from '../../../../services/apiSLTP/versionNotes';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomDateInput from '../../../ui/CustomDateInput';
import CustomTextInput from '../../../ui/CustomTextInput';
import DropDown from '../../../ui/DropDown';
import ModalBody from '../../ModalBody';

interface ModalProps {
    versionNote: VersionNote | null;
    isEditMode?: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    showEditModal: boolean;
    refetch: () => void;
}

const UpdateVersionNoteModal: React.FC<ModalProps> = ({ versionNote, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();
    const [versionName, setVersionName] = useState(versionNote?.versionName || '');
    const [version, setVersion] = useState(versionNote?.version || '');
    const [date, setDate] = useState(versionNote?.date ? new Date(versionNote?.date) : undefined);
    const [newFeatures, setNewFeatures] = useState(versionNote?.newFeatures || '');
    const [upgrades, setUpgrades] = useState(versionNote?.upgrades || '');
    const [bugsCorrections, setBugsCorrections] = useState(versionNote?.bugsCorrections || '');
    const [security, setSecurity] = useState(versionNote?.security || '');
    const [type, setType] = useState(
        versionNote?.type
            ? {
                  value: versionNote?.type.toString(),
                  label: capitalizeFirstLetter(t(getVersionTypeLabel(versionNote?.type))),
              }
            : null
    );

    const { mutate: mutateVersionNote } = useUpdateVersionNoteMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error updating versionNote:', error.message),
    });

    const { mutate: addVersionNote } = useCreateVersionNoteMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error creating versionNote:', error.message),
    });

    return (
        <ModalBody
            title={t('versionNote')}
            subtitle={isEditMode ? t('update') : t('add')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disableSubmit={
                !versionName.trim() || !version.trim() || !newFeatures || !bugsCorrections || !upgrades || !security || !type?.value || !date
            }
            onSubmit={() => {
                isEditMode
                    ? mutateVersionNote({
                          id: versionNote?._id || '',
                          data: {
                              versionName,
                              version,
                              newFeatures,
                              date: date?.toISOString(),
                              bugsCorrections,
                              upgrades,
                              security,
                              type: parseInt(type?.value || ''),
                          },
                      })
                    : addVersionNote({
                          versionName,
                          version,
                          newFeatures,
                          date: date?.toISOString() || '',
                          bugsCorrections,
                          upgrades,
                          security,
                          type: parseInt(type?.value || ''),
                      });
            }}
        >
            <CustomTextInput value={versionName} label={t('name')} onChange={setVersionName} required />
            <CustomTextInput value={version} label={t('version')} onChange={setVersion} required />
            <CustomDateInput label={t('date')} onChange={(value) => (value ? setDate(value) : setDate(undefined))} defaultDate={date} />
            <CustomTextInput value={newFeatures} label={t('newFeatures')} onChange={setNewFeatures} required multiline />
            <CustomTextInput value={upgrades} label={t('upgrades')} onChange={setUpgrades} required multiline />
            <CustomTextInput value={bugsCorrections} label={t('bugsCorrections')} onChange={setBugsCorrections} required multiline />
            <CustomTextInput value={security} label={t('security')} onChange={setSecurity} required multiline />
            <DropDown
                items={Object.values(versionTypeEnum).map((versionType) => ({
                    label: capitalizeFirstLetter(t(getVersionTypeLabel(versionType))),
                    value: versionType.toString(),
                }))}
                selectedItem={type}
                handleChange={(e, value) => {
                    if (value) setType({ value: value?.value, label: value?.label });
                }}
                inputLabel={t('type')}
                required
            />
        </ModalBody>
    );
};

export default UpdateVersionNoteModal;
